/**
 * Safely turn any axios/FastAPI error payload into a human-readable string.
 *
 * FastAPI (Pydantic v2) returns 422 validation errors with a shape like :
 *   { detail: [ { type, loc, msg, input, url }, ... ] }
 * Passing that array directly to `toast.error()` / JSX makes React explode
 * with "Objects are not valid as a React child" because sonner renders the
 * message as children. This helper normalises every known shape into one
 * single string we can safely pass around.
 *
 * Usage :
 *   toast.error(formatApiError(err, 'Échec par défaut'));
 */
export function formatApiError(err, fallback = 'Une erreur est survenue') {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => {
        if (typeof d === 'string') return d;
        if (d && typeof d === 'object') {
          const loc = Array.isArray(d.loc) ? d.loc.filter((x) => x !== 'body').join('.') : '';
          const msg = d.msg || d.message || '';
          return loc ? `${loc}: ${msg}` : msg;
        }
        return '';
      })
      .filter(Boolean);
    if (msgs.length) return msgs.join(' · ');
  }
  if (detail && typeof detail === 'object') {
    if (typeof detail.msg === 'string') return detail.msg;
    try { return JSON.stringify(detail); } catch { /* noop */ }
  }
  if (typeof err?.message === 'string' && err.message) return err.message;
  return fallback;
}

export default formatApiError;
